import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  useToast,
  VStack,
  Badge,
} from '@chakra-ui/react';
import { MdAutoAwesome, MdContentCopy, MdNotificationsActive } from 'react-icons/md';
import { OutlineButton, PrimaryButton, StatusBadge } from '../../../components/ui';
import {
  aiVerifyAppUser,
  getAiVerifyHistoryAppUser,
  sendAppUserNotification,
} from '../../../repositories/appUser.repository';
import { formatDateTimeDisplay } from '../../../utils/personName';

function checkpointColor(status) {
  if (status === 'pass') return 'green';
  if (status === 'fail') return 'red';
  if (status === 'warn') return 'orange';
  return 'gray';
}

function overallBadge(status) {
  if (status === 'ok') return { label: 'OK', status: 'verified' };
  if (status === 'not_ok') return { label: 'Not OK', status: 'error' };
  return { label: 'Needs review', status: 'pending' };
}

function softCheckpointLabel(cp) {
  const id = String(cp?.id || '').toLowerCase();
  const label = String(cp?.label || '').trim();
  if (id === 'fraud_signals' || /fraud/i.test(label)) return 'Document review';
  if (id === 'name_consistency') return 'Name match';
  if (id === 'docs_present') return 'Documents';
  if (id === 'aadhaar_number') return 'Aadhaar number';
  if (id === 'pan_number') return 'PAN number';
  if (id === 'address_consistency') return 'Address';
  if (id === 'photo_identity') return 'Photo check';
  if (id === 'doc_quality') return 'Document clarity';
  if (id === 'gst_number') return 'GST number';
  if (id === 'trade_name') return 'Business name';
  if (id === 'business_photo') return 'Business photo';
  return label || 'Check';
}

/** Soften harsh AI wording for member-facing copy. */
function softenMemberText(text) {
  return String(text || '')
    .replace(/\bKYC failed\b/gi, 'A few details need an update')
    .replace(/\bfailed due to\b/gi, 'needs a quick update for')
    .replace(/\bfraud signals?\b/gi, 'document review')
    .replace(/\bfraud\b/gi, 'document issue')
    .replace(/\brejected\b/gi, 'needs another look')
    .replace(/\binvalid\b/gi, 'does not match')
    .replace(/\ban unrelated\b/gi, 'a mismatched')
    .replace(/\bunrelated\b/gi, 'mismatched')
    .replace(/\bmismatch(es)?\b/gi, 'difference$1')
    .replace(/\s+/g, ' ')
    .trim();
}

function shortDetail(text, max = 100) {
  const t = softenMemberText(text);
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  const breakAt = Math.max(
    cut.lastIndexOf('. '),
    cut.lastIndexOf('; '),
    cut.lastIndexOf(', '),
    cut.lastIndexOf(' '),
  );
  const base = (breakAt > 40 ? cut.slice(0, breakAt) : cut).trimEnd();
  return `${base.replace(/[,;.\s]+$/, '')}.`;
}

/** Fit to notification body limit without mid-sentence cut-off. */
function fitNotificationBody(text, max = 1000) {
  const closing =
    'Once you update the documents or profile details in the app, our team will happily review again. Thank you.';
  const raw = String(text || '').trim();
  if (!raw) return '';
  if (raw.length <= max) return raw;

  let head = raw;
  if (head.endsWith(closing)) {
    head = head.slice(0, -closing.length).trimEnd();
  }

  const budget = Math.max(120, max - closing.length - 2);
  const lines = head.split('\n');
  const kept = [];
  for (const line of lines) {
    const next = kept.length ? `${kept.join('\n')}\n${line}` : line;
    if (next.length > budget) break;
    kept.push(line);
  }
  while (
    kept.length &&
    (!kept[kept.length - 1].trim() || /:$/.test(kept[kept.length - 1].trim()))
  ) {
    kept.pop();
  }
  if (!kept.length) {
    const intro = head.split('\n')[0] || 'A few items need a small update for verification.';
    const trimmedIntro =
      intro.length > budget
        ? `${intro.slice(0, budget - 1).replace(/\s+\S*$/, '').trimEnd()}.`
        : intro;
    return `${trimmedIntro}\n\n${closing}`.slice(0, max);
  }
  return `${kept.join('\n').trim()}\n\n${closing}`.slice(0, max);
}

/** User-facing message for copy / in-app notification (max 1000 chars). */
export function buildAiKycUserMessage(result) {
  if (!result) return '';
  const lines = [];
  const closing =
    'Once you update the documents or profile details in the app, our team will happily review again. Thank you.';

  if (result.overallStatus === 'ok') {
    lines.push(
      'Thank you — your documents look consistent. Our team will complete verification shortly.',
    );
  } else {
    lines.push(
      'Thanks for sharing your details. A few items need a small update so we can continue verification smoothly.',
    );
  }

  const mismatches = Array.isArray(result.mismatches)
    ? result.mismatches.map((m) => shortDetail(m, 120)).filter(Boolean)
    : [];
  if (mismatches.length) {
    lines.push('');
    lines.push('Kindly update these:');
    mismatches.slice(0, 8).forEach((m, i) => lines.push(`${i + 1}. ${m}`));
  } else {
    const failed = (result.checkpoints || []).filter(
      (cp) => cp?.status === 'fail' || cp?.status === 'warn',
    );
    if (failed.length) {
      lines.push('');
      lines.push('Helpful notes:');
      failed.slice(0, 8).forEach((cp) => {
        const label = softCheckpointLabel(cp);
        const detail = shortDetail(cp.detail || '', 100);
        lines.push(`• ${label}${detail ? `: ${detail}` : ''}`);
      });
    }
  }

  lines.push('');
  lines.push(closing);

  return fitNotificationBody(lines.join('\n').trim(), 1000);
}

function historyStatusBadge(entry) {
  if (entry?.status === 'error') {
    return { label: 'Error', colorScheme: 'red' };
  }
  if (entry?.overallStatus === 'ok') {
    return { label: 'OK', colorScheme: 'green' };
  }
  if (entry?.overallStatus === 'not_ok') {
    return { label: 'Not OK', colorScheme: 'red' };
  }
  return { label: 'Needs review', colorScheme: 'orange' };
}

export function AiKycVerifyModal({ isOpen, onClose, user }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [viewingHistory, setViewingHistory] = useState(false);

  const userMessage = useMemo(() => buildAiKycUserMessage(result), [result]);
  const notifyTitle =
    result?.overallStatus === 'ok'
      ? 'Document review update'
      : 'A quick update needed for verification';

  const refreshHistory = async (userId) => {
    if (!userId) return;
    setHistoryLoading(true);
    try {
      const data = await getAiVerifyHistoryAppUser(userId);
      setHistory(Array.isArray(data) ? data : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !user?.id) return;
    refreshHistory(user.id);
  }, [isOpen, user?.id]);

  const run = async () => {
    if (!user?.id) return;
    setLoading(true);
    setError('');
    setResult(null);
    setSelectedHistoryId(null);
    setViewingHistory(false);
    try {
      const data = await aiVerifyAppUser(user.id);
      setResult(data);
      await refreshHistory(user.id);
    } catch (e) {
      setError(e?.message || 'AI verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const openHistoryEntry = (entry) => {
    if (!entry) return;
    setSelectedHistoryId(entry.id);
    setViewingHistory(true);
    setError('');
    if (entry.status === 'error' || !entry.result) {
      setResult(null);
      setError(
        entry.errorMessage ||
          'This run did not produce a usable result.',
      );
      return;
    }
    setResult(entry.result);
  };

  const handleClose = () => {
    setError('');
    setResult(null);
    setHistory([]);
    setSelectedHistoryId(null);
    setViewingHistory(false);
    setLoading(false);
    setSending(false);
    setHistoryLoading(false);
    onClose();
  };

  const copyMessage = async () => {
    if (!userMessage) return;
    try {
      await navigator.clipboard.writeText(userMessage);
      toast({
        title: 'Message copied',
        status: 'success',
        position: 'top-right',
      });
    } catch {
      toast({
        title: 'Could not copy',
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const sendAsNotification = async () => {
    if (!user?.id || !userMessage) return;
    setSending(true);
    try {
      await sendAppUserNotification(user.id, {
        title: notifyTitle,
        body: userMessage,
      });
      toast({
        title: 'Notification sent',
        description: 'Member will see this in their Notifications tab.',
        status: 'success',
        position: 'top-right',
      });
    } catch (err) {
      toast({
        title: err?.message || 'Failed to send notification',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setSending(false);
    }
  };

  const overall = overallBadge(result?.overallStatus);
  const selectedHistory = history.find((h) => h.id === selectedHistoryId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex align="center" gap={2}>
            <MdAutoAwesome />
            Verify by AI
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="13px" color="text.secondary" mb={3}>
            This does not change verification status. Review the checkpoints,
            then use Update status if needed — or send a clear message to the
            member.
          </Text>

          {viewingHistory && selectedHistory ? (
            <Box
              mb={3}
              px={3}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.input"
            >
              <Text fontSize="12px" color="text.secondary">
                Viewing past run:{' '}
                <Text as="span" fontWeight="600" color="text.primary">
                  {formatDateTimeDisplay(selectedHistory.ranAt)}
                </Text>
              </Text>
            </Box>
          ) : null}

          {!result && !loading && !error ? (
            <Box
              p={4}
              borderWidth="1px"
              borderColor="border.subtle"
              borderRadius="md"
              bg="bg.input"
              mb={4}
            >
              <Text fontSize="14px" mb={3}>
                AI will review this member&apos;s profile details plus confirmed
                identity documents (Aadhaar, PAN, GST, business photo, etc.).
              </Text>
              <PrimaryButton
                size="sm"
                leftIcon={<MdAutoAwesome />}
                onClick={run}
              >
                Run AI verify
              </PrimaryButton>
            </Box>
          ) : null}

          {loading ? (
            <Flex align="center" gap={3} py={8} justify="center">
              <Spinner />
              <Text fontSize="14px">Running AI verification…</Text>
            </Flex>
          ) : null}

          {error ? (
            <Box
              p={3}
              borderRadius="md"
              bg="red.50"
              borderWidth="1px"
              borderColor="red.200"
              mb={3}
            >
              <Text fontSize="14px" color="red.700" fontWeight="600">
                {error}
              </Text>
              {!viewingHistory ? (
                <OutlineButton size="sm" mt={3} onClick={run}>
                  Try again
                </OutlineButton>
              ) : null}
            </Box>
          ) : null}

          {result ? (
            <VStack align="stretch" spacing={4} mb={4}>
              <Flex align="center" gap={2} wrap="wrap">
                <StatusBadge status={overall.status}>
                  {overall.label}
                </StatusBadge>
              </Flex>
              <Text fontSize="14px" fontWeight="600">
                {result.summary}
              </Text>

              {Array.isArray(result.mismatches) && result.mismatches.length > 0 ? (
                <Box>
                  <Heading size="xs" mb={2}>
                    Mismatches
                  </Heading>
                  <VStack align="stretch" spacing={1}>
                    {result.mismatches.map((line, idx) => (
                      <Text key={idx} fontSize="13px" color="red.600">
                        • {line}
                      </Text>
                    ))}
                  </VStack>
                </Box>
              ) : null}

              <Box>
                <Heading size="xs" mb={2}>
                  Checkpoints
                </Heading>
                <VStack align="stretch" spacing={2}>
                  {(result.checkpoints || []).map((cp) => (
                    <Box
                      key={cp.id || cp.label}
                      p={3}
                      borderWidth="1px"
                      borderColor="border.subtle"
                      borderRadius="md"
                    >
                      <Flex justify="space-between" gap={2} mb={1} wrap="wrap">
                        <Text fontSize="13px" fontWeight="700">
                          {cp.label || cp.id}
                        </Text>
                        <Badge colorScheme={checkpointColor(cp.status)}>
                          {cp.status}
                        </Badge>
                      </Flex>
                      <Text fontSize="12px" color="text.secondary">
                        {cp.detail}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>

              <Box
                p={3}
                borderWidth="1px"
                borderColor="border.subtle"
                borderRadius="md"
                bg="bg.input"
              >
                <Flex align="center" justify="space-between" gap={2} mb={2} wrap="wrap">
                  <Heading size="xs" mb={0}>
                    Message for member
                  </Heading>
                  <Text fontSize="11px" color="text.secondary">
                    {userMessage.length}/1000
                  </Text>
                </Flex>
                <Textarea
                  value={userMessage}
                  readOnly
                  rows={8}
                  fontSize="13px"
                  bg="bg.surface"
                  borderColor="border.subtle"
                  _focus={{ borderColor: 'utkarsh.amber' }}
                />
                <Flex gap={2} mt={3} wrap="wrap">
                  <OutlineButton
                    size="sm"
                    leftIcon={<MdContentCopy />}
                    onClick={copyMessage}
                  >
                    Copy message
                  </OutlineButton>
                  <PrimaryButton
                    size="sm"
                    leftIcon={<MdNotificationsActive />}
                    onClick={sendAsNotification}
                    isLoading={sending}
                  >
                    Send as notification
                  </PrimaryButton>
                </Flex>
              </Box>

              {Array.isArray(result.docsUsed) && result.docsUsed.length > 0 ? (
                <Text fontSize="12px" color="text.secondary">
                  Docs used:{' '}
                  {result.docsUsed.map((d) => d.docType).join(', ')}
                </Text>
              ) : null}
            </VStack>
          ) : null}

          <Box
            mt={2}
            pt={3}
            borderTopWidth="1px"
            borderColor="border.subtle"
          >
            <Heading size="xs" mb={2}>
              Previous AI verifies
            </Heading>
            {historyLoading ? (
              <Text fontSize="12px" color="text.secondary">
                Loading history…
              </Text>
            ) : history.length === 0 ? (
              <Text fontSize="12px" color="text.secondary">
                No previous AI verify for this member yet.
              </Text>
            ) : (
              <VStack align="stretch" spacing={1.5}>
                {history.map((entry) => {
                  const badge = historyStatusBadge(entry);
                  const isActive = selectedHistoryId === entry.id;
                  return (
                    <Box
                      key={entry.id}
                      as="button"
                      type="button"
                      textAlign="left"
                      w="100%"
                      px={3}
                      py={2}
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor={isActive ? 'utkarsh.amber' : 'border.subtle'}
                      bg={isActive ? 'bg.input' : 'bg.surface'}
                      _hover={{ borderColor: 'utkarsh.amber', bg: 'bg.input' }}
                      onClick={() => openHistoryEntry(entry)}
                    >
                      <Flex align="center" justify="space-between" gap={2} wrap="wrap">
                        <Text fontSize="13px" fontWeight="600">
                          {formatDateTimeDisplay(entry.ranAt)}
                        </Text>
                        <Badge colorScheme={badge.colorScheme} fontSize="10px">
                          {badge.label}
                        </Badge>
                      </Flex>
                      {entry.summary ? (
                        <Text
                          fontSize="12px"
                          color="text.secondary"
                          mt={1}
                          noOfLines={2}
                        >
                          {entry.summary}
                        </Text>
                      ) : entry.errorMessage ? (
                        <Text fontSize="12px" color="red.600" mt={1} noOfLines={2}>
                          {entry.errorMessage}
                        </Text>
                      ) : null}
                    </Box>
                  );
                })}
              </VStack>
            )}
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          {(result || viewingHistory) ? (
            <OutlineButton size="sm" onClick={run} isDisabled={loading || sending}>
              Run again
            </OutlineButton>
          ) : null}
          <PrimaryButton size="sm" onClick={handleClose} isDisabled={sending}>
            Close
          </PrimaryButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
